import React from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import { TOOL_IDS } from '@adsk/alloy-provider';
import { DocumentSpreadsheetIcon } from '@adsk/alloy-react-icon';
import { TabTitle } from '@adsk/exo-skeleton-react';

import { ScheduleActivityCustomOptionLabel } from './components';
import ScheduleCustomDataView from './components/CustomDataView/ScheduleCustomDataView';
import FiltersPanel from './components/FiltersPanel';
import ScheduleActivityCard from './components/ScheduleActivityCard';
import ScheduleActivityEntityBlock from './components/ScheduleActivityEntityBlock';
import ScheduleActivityEntityItem from './components/ScheduleActiviyEntityItem';
import ScheduleCustomErrorState from './components/ScheduleCustomErrorState';
import ScheduleNoDataEmptyState from './components/ScheduleNoDataEmptyState';
import ScheduleNoSearchResultsEmptyState from './components/ScheduleNoSearchResultsEmptyState';
import { ScheduleProvider } from './components/ScheduleProvider';
import ScheduleTopBar from './components/ScheduleTopBar/ScheduleTopBar';
import { nestedOptions } from './helpers';
import {
  scheduleRelationshipsMetadata,
  SCHEDULE_ANALYTICS,
} from './helpers/consts';
import { useTranslation } from './i18n';
import { getEntitiesByIds, queryEntities } from './scheduleSdk';
import { ScheduleActivityReferencePickerPlugin } from './types';

export const scheduleActivityPlugin: ScheduleActivityReferencePickerPlugin = {
  metadata: scheduleRelationshipsMetadata,
  renderIcon: DocumentSpreadsheetIcon.renderIcon,
  toolId: TOOL_IDS.SCHEDULE,
  nestedOptions,
  is3LOSupported: true,
  showSearchBox: true,
  canCreate: () => Promise.resolve(false),
  EntityBlock: ScheduleActivityEntityBlock,
  CustomOptionLabel: ScheduleActivityCustomOptionLabel,
  PluralText: ({ lowerCase }) => {
    const { t } = useTranslation();
    const result = t('SCHEDULE_ACTIVITIES');
    if (lowerCase && typeof result === 'string') {
      return result.toLowerCase();
    }
    return result;
  },
  getEntitiesByIds,
  queryEntities,
  PickerModalTitle: ({ showTabTitle = true }) => {
    const { t } = useTranslation();
    const value = t('ADD_SCHEDULE_ACTIVITY_REFERENCE');

    return (
      <>
        {showTabTitle && <TabTitle value={value as string} />}
        {value}
      </>
    );
  },
  CustomTopBar: ScheduleTopBar,
  AddRelationshipsButtonText: ({ count }) => {
    const { t } = useTranslation();
    const translateKey = count === 1 ? 'ADD_ACTIVITY' : 'ADD_ACTIVITIES';
    return t(translateKey);
  },
  SelectedCount: ({ count }) => {
    const { t } = useTranslation();
    return t('ACTIVITIES_SELECTED', { count });
  },
  CustomDataView: ScheduleCustomDataView,
  CustomPickerEmptyState: ScheduleNoDataEmptyState,
  CustomPickerNoSearchResults: ScheduleNoSearchResultsEmptyState,
  FiltersPanel,
  CustomRemoveEntityModalTitle: () => {
    const { t } = useTranslation();
    return t('REMOVE_REFERENCE_CONFIRMATION_TITLE');
  },
  EntityMenuItem: ScheduleActivityEntityItem,
  CustomPickerError: ScheduleCustomErrorState,
  Provider: ScheduleProvider,
  onRemove: (relationship, entity, projectId) => {
    const type = relationship.entities
      .map((entity) => entity.type)
      .find(
        (entityType: string) =>
          entityType !== scheduleRelationshipsMetadata.type,
      );
    analytics.track(SCHEDULE_ANALYTICS.DELETE, {
      reference_type: type,
      // eslint-disable-next-line spellcheck/spell-checker
      referancing_tool: scheduleRelationshipsMetadata.type,
      project_id: projectId,
    });
  },
  onAddRelationships: (source, entities, projectId) => {
    entities.forEach(() => {
      analytics.track(SCHEDULE_ANALYTICS.ADD, {
        reference_type: source.type,
        // eslint-disable-next-line spellcheck/spell-checker
        referancing_tool: scheduleRelationshipsMetadata.type,
        project_id: projectId,
      });
    });
  },
  ActivityCard: ScheduleActivityCard,
};
